/**
 * MissionDetail — view and edit a saved mission.
 * Shows GPS map (read-only or editable), waypoints panel, linked drone, status.
 */
import { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../api'
import MissionMap, { SelectedPoint, MissionAction, WaypointData } from '../components/MissionMap'
import './MissionDetail.css'

// ─── Types ────────────────────────────────────────────────────────────────────
interface WaypointRaw {
  latitude: number; longitude: number
  altitude?: number; action?: string; index?: number
}
type MissionData = {
  id: string; drone_id: string; name: string; type: string
  waypoints?: WaypointRaw[]
  action_type?: string
  location_lat?: number | string; location_lng?: number | string
  status: string; created_at?: string; created_by?: string
}
type DroneItem = { id: string; drone_id: string; status: string; tail_number?: string }

// ─── Maps ─────────────────────────────────────────────────────────────────────
const ACTION_LABEL: Record<string, string> = {
  land: '🛬 נחיתה', takeoff: '🛫 המראה',
  drop_delivery: '📦 הורדת משלוח', scan: '🔍 סריקה', spray: '💧 ריסוס',
}
const STATUS_LABELS: Record<string, string> = {
  draft: 'טיוטה', active: 'פעיל', completed: 'הושלם', cancelled: 'בוטל',
}
const MISSION_TYPE_LABELS: Record<string, string> = {
  patrol: 'סיור', delivery: 'משלוח', mapping: 'מיפוי',
  inspection: 'בדיקה', survey: 'סקר', zone: 'אזור כיסוי', custom: 'מותאם אישית',
}

export default function MissionDetail() {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()

  const [editing,      setEditing]      = useState(false)
  const [name,         setName]         = useState('')
  const [missionType,  setMissionType]  = useState('patrol')
  const [status,       setStatus]       = useState('draft')
  const [selectedPt,   setSelectedPt]   = useState<SelectedPoint | null>(null)
  const [editWaypoints,setEditWaypoints]= useState<WaypointData[]>([])
  const [saveError,    setSaveError]    = useState('')
  const [saveSuccess,  setSaveSuccess]  = useState(false)
  const [showWpPanel,  setShowWpPanel]  = useState(true)

  const { data: mission, isLoading, error } = useQuery<MissionData>({
    queryKey: ['mission', id],
    queryFn: () => api.get(`/missions/${id}`).then(r => r.data),
    enabled: !!id,
  })
  const { data: drones = [] } = useQuery<DroneItem[]>({
    queryKey: ['drones'],
    queryFn: () => api.get('/drones').then(r => r.data),
  })

  useEffect(() => {
    if (!mission) return
    setName(mission.name)
    setMissionType(mission.type)
    setStatus(mission.status)
    if (mission.location_lat && mission.location_lng && mission.action_type) {
      setSelectedPt({
        lat:        parseFloat(String(mission.location_lat)),
        lng:        parseFloat(String(mission.location_lng)),
        actionType: mission.action_type as MissionAction,
      })
    }
    if (mission.waypoints && mission.waypoints.length > 0) {
      setEditWaypoints(mission.waypoints.map(w => ({
        lat:      w.latitude,
        lng:      w.longitude,
        altitude: w.altitude ?? undefined,
        action:   (w.action as MissionAction | undefined) ?? undefined,
      })))
    }
  }, [mission])

  const updateMutation = useMutation({
    mutationFn: (body: object) => api.put(`/missions/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mission', id] })
      queryClient.invalidateQueries({ queryKey: ['missions'] })
      setSaveSuccess(true); setSaveError(''); setEditing(false)
      setTimeout(() => setSaveSuccess(false), 2500)
    },
    onError: (err: any) => { setSaveError(err.response?.data?.detail || 'שגיאה בשמירה'); setSaveSuccess(false) },
  })

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault()
    const hasWaypoints = editWaypoints.length >= 2
    updateMutation.mutate({
      name:        name.trim(),
      type:        missionType,
      status,
      action_type: hasWaypoints ? (editWaypoints[0].action ?? selectedPt?.actionType ?? null) : (selectedPt?.actionType ?? null),
      location_lat: hasWaypoints ? String(editWaypoints[0].lat) : (selectedPt ? String(selectedPt.lat) : null),
      location_lng: hasWaypoints ? String(editWaypoints[0].lng) : (selectedPt ? String(selectedPt.lng) : null),
      waypoints: hasWaypoints
        ? editWaypoints.map((wp, i) => ({
            latitude: wp.lat, longitude: wp.lng,
            altitude: wp.altitude ?? null,
            action:   wp.action ?? null,
            index:    i + 1,
          }))
        : [],
    })
  }

  const linkedDrone  = drones.find(d => d.id === mission?.drone_id)
  const hasLocation  = !!mission?.location_lat && !!mission?.location_lng
  const hasWaypoints = (mission?.waypoints?.length ?? 0) >= 2
  const isZoneMission = mission?.type === 'zone' && (mission?.waypoints?.length ?? 0) >= 3

  // Convert stored waypoints to WaypointData for map
  const displayWaypoints: WaypointData[] = hasWaypoints
    ? (mission!.waypoints!.map(w => ({
        lat:      w.latitude,
        lng:      w.longitude,
        altitude: w.altitude ?? undefined,
        action:   (w.action as MissionAction | undefined) ?? undefined,
      })))
    : []

  if (!id)       return <p className="page-error">מזהה חסר</p>
  if (isLoading) return <div className="page-loading">טוען...</div>
  if (error || !mission) return (
    <div className="page-error">
      המשימה לא נמצאה. <Link to="/missions">חזרה לרשימה</Link>
    </div>
  )

  return (
    <div className="md-page">

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="md-header">
        <Link to="/missions" className="md-back">← רשימת משימות</Link>
        <div className="md-title-row">
          <h1>{mission.name}</h1>
          <span className={`md-status-badge status-${mission.status}`}>
            {STATUS_LABELS[mission.status] ?? mission.status}
          </span>
          {hasWaypoints && (
            <span className="md-wp-badge">🗺 {mission.waypoints!.length} WP</span>
          )}
        </div>
        {linkedDrone && (
          <div className="md-drone-link">
            <span>רחפן: </span>
            <Link to={`/drones/${linkedDrone.id}`} className="md-drone-anchor">
              ✈ {linkedDrone.drone_id}
              {linkedDrone.tail_number && <span className="md-tail"> ({linkedDrone.tail_number})</span>}
            </Link>
          </div>
        )}
      </div>

      <div className="md-body">

        {/* ── Map section ─────────────────────────────────────────────────── */}
        <section className="md-section panel">
          <div className="md-section-header">
            <h2>{isZoneMission ? '⬡ אזור כיסוי' : hasWaypoints ? '🗺️ מסלול משימה' : '🗺️ מפת משימה'}</h2>
            {!editing && (
              <button className="btn-edit" onClick={() => setEditing(true)}>✏️ עריכה</button>
            )}
          </div>

          {hasLocation || hasWaypoints ? (
            <MissionMap
              mode={isZoneMission ? 'zone' : hasWaypoints ? 'waypoints' : 'single'}
              zoneType={isZoneMission ? mission.action_type as MissionAction : undefined}
              initialPoint={
                !hasWaypoints && hasLocation ? {
                  lat:        parseFloat(String(mission.location_lat)),
                  lng:        parseFloat(String(mission.location_lng)),
                  actionType: mission.action_type as MissionAction | undefined,
                } : undefined
              }
              initialWaypoints={(hasWaypoints || isZoneMission) ? displayWaypoints : undefined}
              onLocationSelect={editing && !hasWaypoints ? setSelectedPt : undefined}
              onWaypointsChange={editing && (hasWaypoints || isZoneMission) ? setEditWaypoints : undefined}
              readOnly={!editing}
            />
          ) : (
            <div className="md-no-location">
              <span>📍 לא הוגדר מיקום יעד למשימה זו</span>
              {!editing && (
                <button className="btn-edit" onClick={() => setEditing(true)}>הוסף מיקום</button>
              )}
            </div>
          )}

          {editing && selectedPt && !hasWaypoints && (
            <div className="md-point-edit-hint">
              <span dir="ltr">📍 {selectedPt.lat.toFixed(6)}, {selectedPt.lng.toFixed(6)}</span>
              {selectedPt.actionType && <strong>{ACTION_LABEL[selectedPt.actionType]}</strong>}
            </div>
          )}
        </section>

        {/* ── Waypoints / zone vertices panel ──────────────────────────────── */}
        {(hasWaypoints || isZoneMission) && (
          <section className="md-section panel">
            <div className="md-section-header">
              <h2>{isZoneMission ? 'נקודות גבול האזור' : 'נקודות מסלול'}</h2>
              <button
                className="btn-edit"
                onClick={() => setShowWpPanel(v => !v)}
              >
                {showWpPanel ? '▲ כווץ' : '▼ הרחב'}
              </button>
            </div>
            {showWpPanel && (
              <div className="md-wp-table-wrap">
                <table className="md-wp-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>קו רוחב / קו אורך</th>
                      {!isZoneMission && <th>גובה</th>}
                      {!isZoneMission && <th>פעולה</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {mission.waypoints!.map((wp, idx) => (
                      <tr key={idx}>
                        <td className="md-wp-num" dir="ltr">
                          {isZoneMission ? `V${idx + 1}` : `WP${idx + 1}`}
                        </td>
                        <td><code className="md-wp-coords" dir="ltr">
                          {wp.latitude.toFixed(5)}, {wp.longitude.toFixed(5)}
                        </code></td>
                        {!isZoneMission && (
                          <td className="md-wp-alt">
                            {wp.altitude != null ? `${wp.altitude} m` : '—'}
                          </td>
                        )}
                        {!isZoneMission && (
                          <td className="md-wp-action">
                            {wp.action ? ACTION_LABEL[wp.action] ?? wp.action : '—'}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {/* ── Details form ─────────────────────────────────────────────────── */}
        <section className="md-section panel">
          <h2>פרטי משימה</h2>
          <form onSubmit={handleSave} className="md-form-grid">

            <label>
              שם משימה
              {editing
                ? <input value={name} onChange={e => setName(e.target.value)} required />
                : <span className="md-value">{mission.name}</span>
              }
            </label>

            <label>
              סוג משימה
              {editing
                ? (
                  <select value={missionType} onChange={e => setMissionType(e.target.value)}>
                    {Object.entries(MISSION_TYPE_LABELS).map(([v, l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </select>
                )
                : <span className="md-value">{MISSION_TYPE_LABELS[mission.type] ?? mission.type}</span>
              }
            </label>

            <label>
              פעולת משימה
              <span className="md-value md-action">
                {mission.action_type ? ACTION_LABEL[mission.action_type] : '—'}
              </span>
            </label>

            <label>
              סטטוס
              {editing
                ? (
                  <select value={status} onChange={e => setStatus(e.target.value)}>
                    {Object.entries(STATUS_LABELS).map(([v, l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </select>
                )
                : <span className="md-value">{STATUS_LABELS[mission.status] ?? mission.status}</span>
              }
            </label>

            {mission.created_at && (
              <label className="md-full">
                נוצר ב
                <span className="md-value">
                  {new Date(mission.created_at).toLocaleString('he-IL')}
                </span>
              </label>
            )}

            {saveError   && <p className="form-error md-full">{saveError}</p>}
            {saveSuccess && <p className="form-success md-full">✅ המשימה עודכנה בהצלחה</p>}

            {editing && (
              <div className="form-actions md-full">
                <button type="button" onClick={() => { setEditing(false); setSaveError('') }}>ביטול</button>
                <button type="submit" className="btn-primary" disabled={updateMutation.isPending}>
                  {updateMutation.isPending ? 'שומר...' : 'שמור שינויים'}
                </button>
              </div>
            )}
          </form>
        </section>
      </div>
    </div>
  )
}
